// src/pages/Dashboard.tsx
// deps: npm i @mui/material @mui/icons-material recharts d3-geo topojson-client
import React, { useMemo, useState } from 'react';
import {
  Box, Grid, Card, CardHeader, CardContent, Typography,
  ToggleButtonGroup, ToggleButton, FormControlLabel, Switch,
  Chip, Stack, useMediaQuery, IconButton, Tooltip as MuiTooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
  BarChart, Bar, LineChart, Line, ReferenceArea
} from 'recharts';

import { Fade, Grow } from "@mui/material";

import * as d3geo from 'd3-geo';
import { feature as topojsonFeature } from 'topojson-client';
import type { FeatureCollection, Feature, Geometry, GeoJsonProperties } from 'geojson';

import { MONTHLY_DATA, MonthlyRow } from '../data';

import {
  loadStateData,
  normalizeState,
  type StateStats
} from '../loadStateData';


// ---------- Formatters ----------
const fmt2 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });
const fmtInt = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

// ---------- Types ----------
type ChartMode = 'bar' | 'line';
type Granularity = 'year' | 'month';

// ---------- Palette ----------
const basePalette = {
  a: '#1976d2',
  b: '#388e3c',
  c: '#fbc02d',
  d: '#d32f2f',
  grid: '#e0e0e0',
};
const palette = {
  ...basePalette,
  forecastStroke: '#2e3b4e99',
  forecastFill: '#2e3b4e22',
};
const CHORO_COLORS = ['#E8F1FF', '#B7D3FF', '#78B0FA', '#3A86F7', '#0F5BD8'];

// ---------- Constants ----------
const INDIA_TOPO_URL = process.env.PUBLIC_URL + '/india_state_geo.json';
const FORECAST_START = 2026;

const bucketize = (value: number, min: number, max: number, steps = CHORO_COLORS.length) => {
  if (!isFinite(value) || max <= min) return 0;
  const ratio = (value - min) / (max - min);
  return Math.min(steps - 1, Math.max(0, Math.floor(ratio * steps)));
};

// Normalize to FeatureCollection (fix TS error for TopoJSON conversion)
function toFeatureCollection(
  x: FeatureCollection<Geometry, GeoJsonProperties> | Feature<Geometry, GeoJsonProperties>
): FeatureCollection<Geometry, GeoJsonProperties> {
  return x.type === 'FeatureCollection' ? x : { type: 'FeatureCollection', features: [x] };
}

// ---------- Reusable UI ----------
const PanelHeader: React.FC<{
  title: string;
  mode?: ChartMode;
  onMode?: (m: ChartMode) => void;
  children?: React.ReactNode;
  hint?: string;
}> = ({ title, mode, onMode, hint, children }) => (
  <CardHeader
    title={
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
        {hint && (
          <MuiTooltip title={hint}>
            <IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton>
          </MuiTooltip>
        )}
      </Stack>
    }
    action={
      <Stack direction="row" spacing={1} alignItems="center">
        {children}
        {mode && onMode && (
          <ToggleButtonGroup size="small" value={mode} exclusive onChange={(_, v) => v && onMode(v)}>
            <ToggleButton value="bar">Bar</ToggleButton>
            <ToggleButton value="line">Line</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>
    }
    sx={{ pb: 0.5, borderBottom: '1px solid #eee' }}
  />
);

const Kpi: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
    <Typography variant="overline" sx={{ opacity: 0.7 }}>{label}</Typography>
    <Typography variant="h5" fontWeight={700}>{value}</Typography>
    {sub && <Typography variant="caption" sx={{ opacity: 0.8 }}>{sub}</Typography>}
  </Card>
);

// ---------- Helpers ----------
const ymLabel = (r: MonthlyRow) => `${r.year}-${String(r.month).padStart(2, '0')}`;
const isForecastYear = (y: number) => y >= FORECAST_START;
const pct = (curr: number, prev?: number) => {
  if (prev === undefined || prev === null || prev === 0) return 0;
  return ((curr - prev) / Math.abs(prev)) * 100;
};

// ---------- Data transforms ----------
function toYearly(monthlyData: MonthlyRow[]): MonthlyRow[] {
  const yearMap: Record<number, MonthlyRow> = {};
  for (const m of monthlyData) {
    if (!yearMap[m.year]) {
      yearMap[m.year] = { ...m };
    } else {
      yearMap[m.year].gdpT += m.gdpT || 0;
      yearMap[m.year].upiTxMillion += m.upiTxMillion || 0;
      yearMap[m.year].moneyCrore += m.moneyCrore || 0;
      yearMap[m.year].urbanHousingMillion += m.urbanHousingMillion || 0;
      yearMap[m.year].smartphonePct = m.smartphonePct;
      yearMap[m.year].urbanPct = m.urbanPct;
    }
  }
  return Object.values(yearMap).sort((a, b) => a.year - b.year);
}

// ============================== MAIN ==============================
const Dashboard: React.FC = () => {
  const downMd = useMediaQuery('(max-width:900px)');
  const [granularity, setGranularity] = useState<Granularity>('year');
  const [gdpIndexed, setGdpIndexed] = useState(false);
  const [showForecast, setShowForecast] = useState(true);
  const [gdpMode, setGdpMode] = useState<ChartMode>('bar');
  const [stateData, setStateData] = useState<Record<string, StateStats>>({});
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [openInsights, setOpenInsights] = useState(false);

  React.useEffect(() => {
    loadStateData().then(setStateData);
  }, []);

  // ---- Build series ----
  const yearly = useMemo(() => toYearly(MONTHLY_DATA), []);
  const yearlyActuals = useMemo(() => yearly.filter(d => !isForecastYear(d.year)), [yearly]);

  const gdpYearSplit = useMemo(() => {
    const rows = yearly.map((d, i) => {
      const prev = i === 0 ? undefined : yearly[i - 1];
      const yoy = pct(d.gdpT, prev?.gdpT);
      return { year: d.year, raw: d.gdpT, yoy };
    });
    const baseVal = gdpIndexed
      ? (rows.find(r => r.year === (yearlyActuals[0]?.year ?? rows[0].year))?.raw ?? rows[0].raw)
      : 1;
    return rows.map(r => {
      const val = gdpIndexed ? (r.raw / baseVal) * 100 : r.raw;
      return {
        x: r.year,
        label: String(r.year),
        gdpActual: !isForecastYear(r.year) ? val : null,
        gdpForecast: isForecastYear(r.year) ? val : null,
        yoy: r.yoy,
      };
    });
  }, [yearly, gdpIndexed, yearlyActuals]);

  const latestYearActual = yearlyActuals[yearlyActuals.length - 1] ?? yearly[yearly.length - 1];

  const monthly = useMemo(() => [...MONTHLY_DATA].sort((a, b) =>
    a.year === b.year ? a.month - b.month : a.year - b.year
  ), []);
  const monthlyActuals = useMemo(() => monthly.filter(m => !isForecastYear(m.year)), [monthly]);

  const gdpMonthSplit = useMemo(() => {
    return monthly.map((m, i) => {
      const prev = i === 0 ? undefined : monthly[i - 1];
      const mom = pct(m.gdpT, prev?.gdpT);
      const baseVal = gdpIndexed ? (monthlyActuals[0]?.gdpT ?? monthly[0].gdpT) : 1;
      const val = gdpIndexed ? (m.gdpT / baseVal) * 100 : m.gdpT;
      return {
        x: m.year * 100 + m.month,
        label: ymLabel(m),
        gdpActual: !isForecastYear(m.year) ? val : null,
        gdpForecast: isForecastYear(m.year) ? val : null,
        yoy: mom,
      };
    });
  }, [monthly, gdpIndexed, monthlyActuals]);

  const gdpSplit = granularity === 'year' ? gdpYearSplit : gdpMonthSplit;

  const latestMonthActual = monthlyActuals[monthlyActuals.length - 1] ?? monthly[monthly.length - 1];
  const latestActual = granularity === 'year'
    ? { label: String(latestYearActual.year), gdpT: latestYearActual.gdpT }
    : { label: ymLabel(latestMonthActual), gdpT: latestMonthActual.gdpT };

  const periodLabel = latestActual.label;
  const forecastBand = granularity === 'year'
    ? { x1: FORECAST_START - 0.5, x2: (yearly[yearly.length - 1]?.year ?? FORECAST_START) + 0.5 }
    : { x1: (FORECAST_START * 100 + 1) - 0.5, x2: (monthly[monthly.length - 1]?.year * 100 + monthly[monthly.length - 1]?.month) + 0.5 };

  const xKey = 'x';
  const xTick = (d: any) => {
    if (granularity === 'year') return String(d);
    const yr = Math.floor(d / 100);
    const mo = d % 100;
    return `${yr}-${String(mo).padStart(2, '0')}`;
  };

  // ---- Map state values ----
  const moneyByState = useMemo(() => {
    const out: Record<string, number> = {};
    Object.entries(stateData).forEach(([k, v]) => { out[k] = v.moneyCr || 0; });
    return out;
  }, [stateData]);

  const phoneByState = useMemo(() => {
    const out: Record<string, number> = {};
    Object.entries(stateData).forEach(([k, v]) => { out[k] = v.smartphonePct || 0; });
    return out;
  }, [stateData]);

  const housingByState = useMemo(() => {
    const out: Record<string, number> = {};
    Object.entries(stateData).forEach(([k, v]) => { out[k] = v.housingIdx || 0; });
    return out;
  }, [stateData]);

  const urbanPctByState = useMemo(() => {
    const out: Record<string, number> = {};
    Object.entries(stateData).forEach(([k, v]) => { out[k] = v.urbanPct || 0; });
    return out;
  }, [stateData]);

  const moneyVals = Object.values(moneyByState);
  const moneyMin = Math.min(...moneyVals);
  const moneyMax = Math.max(...moneyVals);

  // phoneByState, housingByState, urbanPctByState are available for future map layers
  void phoneByState;
  void housingByState;
  void urbanPctByState;

  // Selected-state metrics
  const selKey = normalizeState(selectedState || '');

  type Metrics = {
    title: string;
    moneyCr: number;
    smartphonePct: number;
    urbanPct: number;
    housingIdx: number;
  };

  const metrics: Metrics = useMemo(() => {
    if (selectedState) {
      const state = stateData[selKey];
      if (state) {
        return {
          title: selectedState,
          moneyCr: state.moneyCr || 0,
          smartphonePct: state.smartphonePct || 0,
          urbanPct: state.urbanPct || 0,
          housingIdx: state.housingIdx || 0,
        };
      }
    }
    const allStates = Object.values(stateData);
    const avg = (arr: number[]) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
    const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);
    return {
      title: 'India (overall)',
      moneyCr: sum(allStates.map(s => s.moneyCr || 0)),
      smartphonePct: avg(allStates.map(s => s.smartphonePct || 0)),
      urbanPct: avg(allStates.map(s => s.urbanPct || 0)),
      housingIdx: avg(allStates.map(s => s.housingIdx || 0)),
    };
  }, [selectedState, selKey, stateData]);

  return (
    <Box p={{ xs: 2, md: 4 }} sx={{ bgcolor: '#eef3ff', minHeight: '100vh' }}>
      {/* Global controls + KPI */}
      <Fade in={true} timeout={600}>
  <div>
      <Stack
        direction={downMd ? 'column' : 'row'}
        spacing={2}
        mb={2}
        alignItems={downMd ? 'stretch' : 'center'}
        justifyContent="space-between"
      >
        {/* Left side: KPIs */}
        <Stack direction="row" spacing={2} flex={1} flexWrap="wrap">
          <Kpi
            label={`GDP (${latestActual.label}, USD)`}
            value={`$${new Intl.NumberFormat('en-IN').format(418e9)}`}
            sub={granularity === 'year' ? 'Annual nominal GDP' : 'Monthly GDP snapshot'}
          />

        </Stack>

        {/* Right side: Controls */}
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
          <ToggleButtonGroup
            size="small"
            value={granularity}
            exclusive
            onChange={(_, v) => v && setGranularity(v)}
          >
            <ToggleButton value="year">Yearly</ToggleButton>
            <ToggleButton value="month">Monthly</ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel
            control={<Switch checked={showForecast} onChange={e => setShowForecast(e.target.checked)} />}
            label={<Typography variant="body2">Show forecast</Typography>}
          />
        </Stack>
      </Stack>
      </div>
      </Fade>

      {/* === Layout: Left map; Right split (Metrics top, GDP bottom) === */}
      <Grid container spacing={2}>
        {/* LEFT: Map */}
        <Grid item xs={12} md={5} lg={5}>
          <Card sx={{
            height: { xs: 420, md: 800 },
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            background: 'radial-gradient(1200px 400px at -10% -20%, #F7FAFF 0%, #FFFFFF 40%)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
          }}>
            <PanelHeader title={`Money by state — ${periodLabel}`} hint="Click a state to drive the metrics on the right." />
            <CardContent sx={{ height: { xs: 360, md: 700 }, p: 0 }}>
              <Box sx={{ width: '100%', height: '100%' }}>
                <svg viewBox="0 0 1000 800" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
                  <ChoroplethSVG
                    topoUrl={INDIA_TOPO_URL}
                    valuesByState={moneyByState}
                    min={moneyMin}
                    max={moneyMax}
                    bucketColors={CHORO_COLORS}
                    valueLabeler={(v) => `₹${fmtInt.format(v)} Cr`}
                    onSelect={(name) => setSelectedState(name)}
                    selectedKey={selKey}
                  />
                </svg>
              </Box>

              {/* Legend */}
              <Stack spacing={0.5} mt={0.5} sx={{ px: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Legend :</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>₹ in Crores, Darker = higher</Typography>
                </Stack>
                <Box>
                  <Box sx={{
                    height: 12,
                    borderRadius: 1.5,
                    background: `linear-gradient(90deg, ${CHORO_COLORS.join(',')})`,
                    border: '1px solid',
                    borderColor: 'divider',
                  }} />
                  <Stack direction="row" justifyContent="space-between" mt={0.5}>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>{fmtInt.format(moneyMin)}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>{fmtInt.format(moneyMax)}</Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT: split vertically */}
        <Grid item xs={12} md={7} lg={7}>
          <Stack spacing={2} sx={{ height: '100%' }}>

            {/* BOTTOM: Metrics */}
            <Card sx={{
              width: "100%",
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              background: 'radial-gradient(1200px 400px at -10% -20%, #F7FAFF 0%, #FFFFFF 40%)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
            }}>
              <PanelHeader
                title={`State metrics — ${metrics?.title ?? 'India (overall)'}`}
                hint="These are proxy values fed from the map click (urbanisation, housing, smartphone)."
              />
              <CardContent
  sx={{
    px: { xs: 1, sm: 2 },
    py: { xs: 2, sm: 3 },
  }}
>
                 <Grid container
  spacing={2}
  justifyContent="center"
>
                  {metrics && [
                    { label: 'Money transferred', value: `₹${fmtInt.format(metrics.moneyCr)} Cr`, sub: periodLabel },
                    { label: 'Smartphone penetration', value: `${fmt2.format(metrics.smartphonePct)}%`, sub: '% of population' },
                    { label: 'Urbanisation', value: `${fmt2.format(metrics.urbanPct)}%`, sub: '% urban population' },
                    { label: 'Housing index', value: `${fmt2.format(metrics.housingIdx)}`, sub: 'Relative score' },
                  ].map((item, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box sx={{
                        height: 90,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 2,
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { transform: 'scale(1.03)', boxShadow: 3, cursor: 'pointer', backgroundColor: '#E3F2FD' },
                        p: 0.75,
                      }}>
                        <Box
  sx={{
    textAlign: "center",
    width: "100%",
  }}
>
                         <Typography variant="overline" sx={{ opacity: 0.7 }}>
  {item.label}
</Typography>

<Typography
  variant="h6"
  fontWeight={700}
  sx={{ fontSize: { xs: "1rem", sm: "1.2rem" } }}
>
  {item.value}
</Typography>

{item.sub && (
  <Typography variant="caption" sx={{ opacity: 0.8 }}>
    {item.sub}
  </Typography>
)}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {!selectedState && (
                  <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', fontStyle: 'italic', opacity: 0.7 }}>
                    Select a state on the map to explore regional insights!
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* TOP: GDP chart */}
            <Card sx={{
              flex: 2,
              height: "100%",
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              background: 'radial-gradient(1200px 400px at -10% -20%, #F7FAFF 0%, #FFFFFF 40%)',
              mt: 2,
              pt: 4,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
            }}>
              <PanelHeader
                title={`GDP — ${granularity === 'year' ? 'size & YoY' : 'size & Δ% MoM'}`}
                mode={gdpMode}
                onMode={setGdpMode}
                hint={granularity === 'year' ? 'Actuals to 2025; 2026–2028 shaded as forecast.' : 'Monthly levels; 2026+ shaded as forecast.'}
              >
                <FormControlLabel
                  control={<Switch checked={gdpIndexed} onChange={(e) => setGdpIndexed(e.target.checked)} />}
                  label={<Typography variant="body2">Normalize (index @ first actual)</Typography>}
                />
              </PanelHeader>
              
              <CardContent
  sx={{
    height: { xs: 260, sm: 320, md: 360 },
    px: { xs: 1, sm: 2 },
  }}
>
                <ResponsiveContainer width="100%" height="100%">
                  {gdpMode === 'bar' ? (
                    <BarChart data={gdpSplit} margin={{ top: 8, right: 10, left: -10, bottom: 8 }}>
                      <CartesianGrid stroke={palette.grid} strokeOpacity={0.4} />
                      <XAxis dataKey={xKey} tickFormatter={xTick} />
                      <YAxis tickFormatter={(v) => gdpIndexed ? `${Math.round(v as number)}` : `$${(v as number).toFixed(2)}T`} />
                      <Tooltip
                        contentStyle={{ borderRadius: 8, fontSize: 12 }}
                        formatter={(value: any) => gdpIndexed ? value : `$${value.toFixed(2)}T`}
                      />
                      <Legend />
                      <Bar dataKey="gdpActual" name={gdpIndexed ? 'GDP (Index)' : 'GDP (Trillions)'} fill={palette.a} />
                      {showForecast && (
                        <Bar dataKey="gdpForecast" name="GDP (Forecast)" fill={palette.forecastFill} stroke={palette.forecastStroke} />
                      )}
                      <Line type="monotone" dataKey="yoy" name={granularity === 'year' ? 'YoY %' : 'Δ% MoM'} stroke={palette.c} dot={false} />
                      {showForecast && (
                        <ReferenceArea x1={forecastBand.x1} x2={forecastBand.x2} strokeOpacity={0} fill={palette.forecastFill} />
                      )}
                    </BarChart>
                  ) : (
                    <LineChart data={gdpSplit}margin={{ top: 8, right: 10, left: -10, bottom: 8 }}>
                      <CartesianGrid stroke={palette.grid} />
                     <XAxis
  dataKey={xKey}
  tickFormatter={xTick}
  tick={{ fontSize: 10 }}
  interval="preserveStartEnd" />
                      <YAxis
  width={50}
  tick={{ fontSize: 10 }}
  tickFormatter={(v) =>
    gdpIndexed
      ? `${Math.round(v as number)}`
      : `$${(v as number).toFixed(1)}T`
  }
/>
                      <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }}/>
                      <Legend />
                      <Line type="monotone" dataKey="gdpActual" name={gdpIndexed ? 'GDP (Index)' : 'GDP (Trillions)'} stroke={palette.a} strokeWidth={3} dot={false} />
                      {showForecast && (
                        <Line type="monotone" dataKey="gdpForecast" name="GDP (Forecast)" stroke={palette.forecastStroke} strokeDasharray="6 6" strokeWidth={3} dot />
                      )}
                      <Line type="monotone" dataKey="yoy" name={granularity === 'year' ? 'YoY %' : 'Δ% MoM'} stroke={palette.c} strokeDasharray="4 4" />
                      {showForecast && (
                        <ReferenceArea x1={forecastBand.x1} x2={forecastBand.x2} strokeOpacity={0} fill={palette.forecastFill} />
                      )}
                    </LineChart>
                  )}
                </ResponsiveContainer>
                <Stack
  direction="row"
  spacing={1}
  mt={1}
  flexWrap="wrap"
  justifyContent="center"
>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

// ====================== Map components ======================
type ChoroplethProps = {
  topoUrl: string;
  valuesByState: Record<string, number>;
  min: number;
  max: number;
  bucketColors: string[];
  valueLabeler: (v: number) => string;
  onSelect?: (displayName: string, key: string) => void;
  selectedKey?: string | null;
};

const ChoroplethSVG: React.FC<ChoroplethProps> = ({
  topoUrl, valuesByState, min, max, bucketColors, valueLabeler, onSelect, selectedKey
}) => {
  const [paths, setPaths] = React.useState<Array<{ d: string; name: string; key: string; value: number; fill: string }>>([]);
  const [hoveredKey, setHoveredKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(topoUrl);
        if (!res.ok) throw new Error(`Failed to load map: ${res.status} ${res.statusText}`);
        const raw: any = await res.json();

        let fc: FeatureCollection<Geometry, GeoJsonProperties>;
        if (raw?.type === 'FeatureCollection') {
          fc = raw as FeatureCollection<Geometry, GeoJsonProperties>;
        } else if (raw?.objects) {
          const objMap = raw.objects as Record<string, any>;
          const keys = Object.keys(objMap);
          if (!keys.length) throw new Error('No objects in TopoJSON');
          const preferred = keys.find(k => /state|adm1|layer/i.test(k)) ?? keys[0];
          const anyFeat = topojsonFeature(raw as any, objMap[preferred] as any) as FeatureCollection<Geometry, GeoJsonProperties> | Feature<Geometry, GeoJsonProperties>;
          fc = toFeatureCollection(anyFeat);
        } else {
          throw new Error('Unsupported map file format');
        }

        if (!fc?.features?.length) throw new Error('Empty FeatureCollection');

        const projection = d3geo.geoMercator().fitSize([1000, 800], fc as any);
        const geoPath = d3geo.geoPath(projection);

        const next: Array<{ d: string; name: string; key: string; value: number; fill: string }> = [];
        for (const f of fc.features) {
          const props: any = f.properties || {};
          const rawName = props.st_nm ?? props.ST_NM ?? props.NAME_1 ?? props.NAME ?? 'Unknown';
          const key = normalizeState(String(rawName));
          const val = valuesByState[key] ?? 0;
          const idx = bucketize(val, min, max, bucketColors.length);
          const fill = bucketColors[idx] || '#ccc';
          const d = geoPath(f as any) || '';
          if (d) next.push({ d, name: String(rawName), key, value: val, fill });
        }

        if (!cancelled) setPaths(next);
      } catch (err) {
        console.error('Choropleth render error:', err);
        if (!cancelled) setPaths([]);
      }
    })();
    return () => { cancelled = true; };
  }, [topoUrl, valuesByState, min, max, bucketColors]);

  return (
    <g>
      {paths.map((p, i) => {
        const isSelected = selectedKey ? p.key === selectedKey : false;
        const isHovered = p.key === hoveredKey;
        return (
          <path
            key={i}
            d={p.d}
            fill={isHovered ? '#5aa3f5' : p.fill}
            stroke={isSelected ? '#0F5BD8' : isHovered ? '#0F5BD8' : '#ffffff'}
            strokeWidth={isSelected ? 2 : isHovered ? 1.2 : 0.6}
            style={{
              cursor: 'pointer',
              // NO transform/scale here — that's what causes the flicker
              filter: isHovered ? 'drop-shadow(0 0 6px rgba(15,91,216,0.35))' : 'none',
              transition: 'fill 120ms ease, stroke 120ms ease, filter 120ms ease',
            }}
            onMouseEnter={() => setHoveredKey(p.key)}
            onMouseLeave={() => setHoveredKey(null)}
            onClick={() => onSelect?.(p.name, p.key)}
          >
            <title>{`${p.name}: ${valueLabeler(p.value)}`}</title>
          </path>
        );
      })}
    </g>
  );
};

export default Dashboard;